// Intent: goals were create-only — owner reported "nie da się edytować celu".
// Edit screen per Stitch mock edycja_celu: name, target amount, target date,
// icon grid, and a two-tap delete (Alert.alert is a no-op on web).

import { router, useLocalSearchParams } from 'expo-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyboardAvoidingView, Platform, Pressable, ScrollView, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';

import { AppText } from '@/components/ui/app-text';
import { DateField } from '@/components/ui/date-field';
import { PrimaryButton } from '@/components/ui/primary-button';
import { TextField } from '@/components/ui/text-field';
import { GoalIconPicker, goalIconOf } from '@/components/goal-icon-picker';
import { useGoalsStore } from '@/store/goals-store';
import { colors, spacing } from '@/theme/tokens';

/** "45000" / "45 000" -> grosze (integer złoty input) */
function parseZl(raw: string): number {
  const digits = raw.replace(/[^\d]/g, '');
  return digits ? parseInt(digits, 10) * 100 : 0;
}

export default function EditGoalScreen() {
  const { t } = useTranslation();
  const { id } = useLocalSearchParams<{ id: string }>();
  const goals = useGoalsStore((s) => s.goals);
  const updateGoal = useGoalsStore((s) => s.updateGoal);
  const deleteGoal = useGoalsStore((s) => s.deleteGoal);

  const goal = goals.find((g) => g.id === id);

  const [name, setName] = useState(goal?.name ?? '');
  const [targetRaw, setTargetRaw] = useState(goal ? String(Math.round(goal.targetGr / 100)) : '');
  const [targetDate, setTargetDate] = useState<string | null>(goal?.targetDate ?? null);
  const [icon, setIcon] = useState<string | null>(goal ? goalIconOf(goal) : null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (!goal) return null;

  const targetGr = parseZl(targetRaw);
  const valid = name.trim().length > 0 && targetGr > 0;

  const onSave = async () => {
    await updateGoal(goal.id, { name: name.trim(), targetGr, targetDate, icon });
    router.back();
  };

  // Two-tap delete: first tap arms the button, second actually deletes.
  const onDelete = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    await deleteGoal(goal.id);
    // Jump past the details screen of the goal that no longer exists.
    router.dismissAll();
  };

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top', 'bottom']}>
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <ScrollView
          keyboardShouldPersistTaps="handled"
          contentContainerStyle={{ padding: spacing.screen, gap: spacing.md, paddingBottom: spacing.xl }}
        >
          {/* Header per mock: X on the left, centered title */}
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: spacing.base }}>
            <Pressable
              accessibilityRole="button"
              onPress={() => router.back()}
              style={{ minWidth: 44, minHeight: 44, justifyContent: 'center' }}
            >
              <Ionicons name="close" size={26} color={colors.navy} />
            </Pressable>
            <AppText variant="headlineMd" color={colors.navy} style={{ flex: 1, textAlign: 'center' }}>
              {t('editGoal.title')}
            </AppText>
            <View style={{ minWidth: 44 }} />
          </View>

          <TextField label={t('newGoal.nameLabel')} value={name} onChangeText={setName} />

          <TextField
            label={t('newGoal.targetLabel')}
            placeholder="np. 8 000"
            keyboardType="numeric"
            value={targetRaw}
            onChangeText={setTargetRaw}
          />

          <DateField
            label={t('editGoal.dateLabel')}
            value={targetDate}
            onChange={setTargetDate}
            minIso={new Date().toISOString().slice(0, 10)}
          />

          <AppText variant="labelLg" color={colors.navy}>
            {t('editGoal.iconLabel')}
          </AppText>
          <GoalIconPicker value={icon} onChange={setIcon} />

          <PrimaryButton label={t('editGoal.save')} disabled={!valid} onPress={onSave} />

          <Pressable
            accessibilityRole="button"
            onPress={onDelete}
            style={{ minHeight: 44, alignItems: 'center', justifyContent: 'center' }}
          >
            <AppText variant="labelLg" color={colors.error}>
              {confirmDelete ? t('editGoal.deleteConfirm') : t('editGoal.delete')}
            </AppText>
          </Pressable>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}
